// Per-day, per-case manual markers for the Board (a LOCAL display overlay only — localStorage).
//
// Two independent flags per operating-room SCHEDULE (case), each driving a border on the case block:
//   nerveBlock -> yellow border  (a case that needs a regional / nerve block)
//   pediatric  -> hot-pink border (patient age ≤ 12)
// A case can carry BOTH at once (yellow border + pink ring).
//
// Each flag is an EXPLICIT OVERRIDE: when present (true/false) it overrides the AUTOMATIC detection;
// when absent the board falls back to auto. Auto nerve-block comes from the surgery-type keywords
// (needsNerveBlock). Pediatric has NO auto-detection — we deliberately don't collect or persist patient
// age from customer data, so it is manual-only until a runner hand-flags it. This lets the runner force
// a marker ON (e.g. a block the keywords miss, or a case known to be pediatric), force it OFF, or clear
// back to auto. Markers never touch a persisted allocation — they are purely visual — and are keyed by
// scheduleId, cleared automatically when a flag reverts to auto. Mirrors slotMarks.ts.

export type CaseMarkerKind = 'nerveBlock' | 'pediatric';
export interface CaseMarker { nerveBlock?: boolean; pediatric?: boolean }

const keyFor = (date: string) => `oc-case-markers-${date}`;

export function loadCaseMarkers(date: string): Record<string, CaseMarker> {
  try {
    const raw = localStorage.getItem(keyFor(date));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function getCaseMarker(marks: Record<string, CaseMarker>, scheduleId: string): CaseMarker | undefined {
  return marks[scheduleId];
}

// Set (value true/false) or clear (value null) ONE flag on a case. When a case has no remaining
// overrides its entry is dropped. Returns a NEW object so React state updates re-render.
export function setCaseMarker(date: string, scheduleId: string, kind: CaseMarkerKind, value: boolean | null): Record<string, CaseMarker> {
  const marks = loadCaseMarkers(date);
  const cur: CaseMarker = { ...(marks[scheduleId] || {}) };
  if (value === null) delete cur[kind];
  else cur[kind] = value;
  if (cur.nerveBlock === undefined && cur.pediatric === undefined) delete marks[scheduleId];
  else marks[scheduleId] = cur;
  try { localStorage.setItem(keyFor(date), JSON.stringify(marks)); } catch { /* best-effort */ }
  return { ...marks };
}
